import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import {
  Itinerary,
  ItineraryAttraction,
} from '../../trip-planner/models/itinerary';
import { ButtonTriggerEvent } from '../../../common/components/button/button.component';
import { VisitorEvent } from '../models/visitor.event';
import { VisitorPlace } from '../models/visitor.place';
import { ScriptService } from '../../../common/services/load.script/load.script.service';
import { ConfigService } from '../../../common/services/config/config.service';
import { ImageService } from '../../../common/services/image/image.service';
import { VisitorService } from './visitor.service';
import {
  clone,
  isNullOrUndefined,
  upperFirstLetter,
} from '../../../common/utils/object.extensions';
import { GoogleMapService, TravelMode } from './google.map.service';
import { EventTimeService } from '../../event-calendar/services/event.time.service';
import { removeHtmlTags } from '../../../common/utils/string.extensions';
import {
  TripAttraction,
  TripDay,
  UserTrip,
} from '../models/vititor.trip.storage.command';
import { TripPlannerService } from '../../trip-planner/services/trip.planner.service';

@Injectable({
  providedIn: 'root',
})
export class ExportPDFService {
  public doc: any;
  public pdfImageBase64: Array<PDFImgDataObject>;
  public defaultTopImages = {};
  public defaultTitleText = {};
  public textDefaultColor = '#404040';
  public bannerHeight = 28;
  public pageNumber = 1;
  public y = 0;
  public maxY = 0; // indicate the maxY value for the same row of info. For example, each item name should be in the same row, so we need to know which one has the max lines (maxY), then the next info should be below this maxY
  public itemInfoToPrint = {
    event: ['image', 'name', 'time', 'organizer', 'address', 'more'],
    place: ['image', 'name', 'category', 'rating', 'address', 'more'],
  };
  public errorFallbackImage =
    'https://iti-images.s3.amazonaws.com/events/6ae81b06-ce41-9f93-af1b-cb4f60d749a8.png';
  public command: ExportPDFCommandBase;
  private renderer: Renderer2;

  // constructor(private imageService: ImageService, protected apiService: VisitorApiService, private googleMapService: GoogleMapsService,) {
  constructor(
    public visitorService: VisitorService,
    public imageService: ImageService,
    public timeService: EventTimeService,
    public tripService: TripPlannerService,
    public googleMapService: GoogleMapService,
    public scriptService: ScriptService,
    private rendererFactory: RendererFactory2
  ) {
    this.pdfImageBase64 = [];
    // this.defaultTopImages = {
    //   event: config.PDF.PDFTopEventBackgroundImage,
    //   place: config.PDF.PDFTopPlaceBackgroundImage,
    //   tour: config.PDF.PDFTopTripBackgroundImage,
    //   trip: config.PDF.PDFTopTripBackgroundImage,
    // };
    this.defaultTitleText = {
      event: { text: "Events You Can't Miss", x: 6 },
      place: { text: 'Places to Visit', x: 18 },
      tour: { text: 'Visit Our Suggested Itineraries', x: 3, fontSize: 16 },
      trip: { text: 'Welcome to My Trip', x: 10 },
    };

    this.renderer = this.rendererFactory.createRenderer(null, null); // null, null is generally okay for services

    const SCRIPT_PATH = `https://unpkg.com/jspdf@latest/dist/jspdf.umd.min.js`;

    if (!this.scriptService.isScriptExist(SCRIPT_PATH)) {
      const scriptElement = this.scriptService.loadJsScript(
        this.renderer,
        SCRIPT_PATH
      );
      scriptElement.onload = () => {
        console.log('jspdf script loaded');
      };
      scriptElement.onerror = () => {
        console.log('Could not load the script: ' + SCRIPT_PATH);
      };
    }

    const html2canvas_PATH = `https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js`;

    if (!this.scriptService.isScriptExist(html2canvas_PATH)) {
      const scriptElement = this.scriptService.loadJsScript(
        this.renderer,
        html2canvas_PATH
      );
      scriptElement.onload = () => {
        console.log('html2canvas script loaded');
      };
      scriptElement.onerror = () => {
        console.log('Could not load the script: ' + html2canvas_PATH);
      };
    }
  }

  // public async exportPDF(command: ExportEventPlacePDFCommand) {
  //   console.log(`%c${command.items.length}`, "color:rgba(133,108,217,1);font-size:30px")
  //   this.createDoc(command);
  //
  //   await this.drawHeader(command);
  //
  //   await this.drawTopImageAndTitle(command);
  //   await this.loadImageForItems(command.items, command.items.length, command.progressCallback);
  //
  //   this.y += 10;
  //   const rowGroups = this.putItemsIntoRows();
  //
  //   let row = [];
  //   for (let i = 0; i < rowGroups.length; i++) {
  //     row = rowGroups[i];
  //
  //     // first page has 2 rows of items, other pages have 3 rows
  //     if (i === 2 || (i > 2 && (i - 2) % 3 === 0)) {
  //       this.addPage(command);
  //     }
  //
  //     // in order to make the same info in the same row (x alignment), here we need to print each info for all items in this row one by one, and then print the next info
  //     for (const info of this.itemInfoToPrint[command.type]) {
  //       const drawFunc = this.getDrawItemInfoFunc(info);
  //       for (let j = 0; j < row.length; j++) {
  //         const x = 10 + 50 * j;
  //         drawFunc && drawFunc.call(this, row[j], x);
  //       }
  //       this.y = this.maxY;
  //     }
  //     // row margin
  //     this.y += 10;
  //   }
  //
  //   this.savePDF(command);
  // }

  public async exportItineraryPDF(command: ExportItineraryPDFCommand) {
    this.command = command;
    this.createDoc(command);
    // await this.drawHeader(command);
    await this.drawTopImageAndTitle(command);

    const totalItemsCount = this.calculateTotalAttractionCount(command.trips);

    let tripIndex = 0;
    for (const trip of command.trips) {
      this.addNewPageIfNeeded(command, 180);

      this.drawTripTitle(trip.title.toString());
      const directionData = await this.drawDirectionMap(trip, command.appKey);
      if (!directionData) {
        if (!trip.attractions || !trip.attractions.length) {
          this.drawNoStops();
          continue;
        } else {
          console.log('Error when getting direction map');
        }
      } else {
        this.drawTripInfo(trip, directionData);
      }

      this.drawDescription(trip.description);

      this.addNewPageIfNeeded(command, 250);

      this.pdfImageBase64 = [];
      await this.loadImageForItems(
        trip.attractions.map((x) => x),
        totalItemsCount,
        0,
        command.progressCallback,
        command.logo || this.errorFallbackImage
      );

      if (!this.pdfImageBase64.length) {
        continue;
      }

      this.addNewPageIfNeeded(command, 230);
      this.drawStart();
      this.addNewPageIfNeeded(command, 250);
      this.drawItineraryAttractions(command, directionData);
      // this.addNewPageIfNeeded(command, 250);
      // do not print the END label on a new page
      this.y < 280 && this.drawEnd();

      tripIndex !== command.trips.length - 1 &&
        this.addNewPageIfNeeded(command, 200);
      tripIndex++;
    }

    this.savePDF(command);
  }

  public async exportTripPDF(command: ExportTripPDFCommand) {
    const totalItemsCount = this.calculateTotalTripAttractionCount(
      command.trip
    );
    command.progressCallback(3);

    await this.generateBannerImage(command, totalItemsCount);
    let imageInBanner = totalItemsCount;
    imageInBanner = totalItemsCount > 4 ? 4 : imageInBanner;

    this.command = command;
    this.createDoc(command);

    let progressStep =
      ((imageInBanner + 1) / (totalItemsCount + imageInBanner + 1)) * 100;
    command.progressCallback(progressStep);

    await this.drawTopImageAndTitle(command, 100);
    progressStep =
      ((imageInBanner + 2) / (totalItemsCount + imageInBanner + 1)) * 100;
    command.progressCallback(progressStep);

    for (const day of command.trip.days) {
      await this.loadImageForItems(
        day.attractions,
        totalItemsCount,
        imageInBanner,
        command.progressCallback,
        command.logo || this.errorFallbackImage
      );
    }
    command.progressCallback(95);

    this.drawTripTitle(command.trip.title);
    let index = 1;
    for (const day of command.trip.days) {
      this.addNewPageIfNeeded(command, 180);

      const dayTitle = `Day ${index} (${day.dateText})`;
      const offsetX = this.getCenteredTextX(dayTitle, 210);
      this.y = this.drawText({
        text: dayTitle,
        x: offsetX + (index === 1 ? 12 : 0),
        y: this.y + 7,
      } as DrawTextCommand);

      const directionData = await this.drawDirectionMap(day, command.appKey);
      if (!directionData) {
        if (!day.attractions || !day.attractions.length) {
          this.drawNoStops();
        } else {
          console.log('Error when getting direction map');
        }
      } else {
        this.drawTripInfo(day, directionData);
      }

      this.addNewPageIfNeeded(command, 250);
      this.drawTripAttractions(day);
      index++;
    }
    command.progressCallback(100);
    document.getElementById('ig-tp-pdf-trip-banner')?.remove();

    this.savePDF(command);
  }

  async generateBannerImage(
    command: ExportTripPDFCommand,
    totalItemsCount: number
  ) {
    const base64Imgs = await this.cropImages(command, totalItemsCount);
    const dynamicHTML = this.createBannerDiv(base64Imgs);
    document.body.appendChild(dynamicHTML);

    const div = document.getElementById('ig-tp-pdf-trip-banner');
    const canvas = await window['html2canvas'](div);
    command.topImage = canvas.toDataURL();
  }

  async cropImages(command: ExportTripPDFCommand, totalItemsCount: number) {
    let base64Imgs = [],
      imageInBanner = totalItemsCount;
    if (totalItemsCount >= 4) {
      imageInBanner = 4;
    }

    let index = 0;
    for (const day of command.trip.days) {
      for (const attr of day.attractions) {
        if (base64Imgs.length >= 4) break;

        let desiredWidth = 840,
          desiredHeight = 480;
        if (base64Imgs.length === 0 && totalItemsCount > 1) {
          desiredWidth = 420;
          desiredHeight = 480;
        }
        if (base64Imgs.length === 1) {
          if (totalItemsCount === 2) {
            desiredWidth = 420;
            desiredHeight = 480;
          }
          if (totalItemsCount > 2) {
            desiredWidth = 420;
            desiredHeight = 240;
          }
        }
        if (base64Imgs.length === 2) {
          if (totalItemsCount === 3) {
            desiredWidth = 420;
            desiredHeight = 240;
          }
          if (totalItemsCount > 3) {
            desiredWidth = 210;
            desiredHeight = 240;
          }
        }
        if (base64Imgs.length === 3) {
          desiredWidth = 210;
          desiredHeight = 240;
        }
        console.log(
          `desiredWidth: ${desiredWidth}, desiredHeight: ${desiredHeight}`
        );

        const base64 = await this.imageService.cropImage(
          attr.item.cover.source,
          desiredWidth * 2,
          desiredHeight * 2
        );
        index++;
        command.progressCallback(
          (index / (totalItemsCount + imageInBanner)) * 100
        );
        base64Imgs.push(base64);
      }
    }
    return base64Imgs;
  }

  /**
   this is AI generated, basically, it generates a div contains 1-4 images (from the first 4 attractions in user trip) and creates the banner layout
   this div will be used by html2canvas to generate the banner image
   */
  createBannerDiv(imageUrls: string[]) {
    const container = document.createElement('div');
    container.className =
      'fixed z-[-1] translate-x-[-3000px] top-2 h-fit w-fit';
    container.id = 'ig-tp-pdf-trip-banner';

    const flexContainer = document.createElement('div');
    flexContainer.className = 'flex items-center justify-center h-fit w-fit';

    const innerContainer = document.createElement('div');
    innerContainer.className =
      'w-[1260px] h-[720px] flex gap-1.5 bg-white shadow-lg';

    if (imageUrls.length === 0) {
      return null; // Or handle the case where there are no images differently
    }

    const firstImageDiv = document.createElement('div');
    firstImageDiv.className =
      imageUrls.length === 1 ? 'w-full h-full' : 'w-1/2 h-full';

    const firstImage = document.createElement('img');
    firstImage.src = imageUrls[0];
    firstImage.className = 'w-full h-full object-cover';
    firstImage.alt = 'Left image';
    firstImageDiv.appendChild(firstImage);
    innerContainer.appendChild(firstImageDiv);

    if (imageUrls.length > 1) {
      const rightHalfDiv = document.createElement('div');
      rightHalfDiv.className =
        imageUrls.length === 2
          ? 'w-1/2 h-full'
          : 'w-1/2 h-full flex flex-col gap-1.5';

      if (imageUrls.length === 2) {
        const secondImageDiv = document.createElement('div');
        secondImageDiv.className = 'h-full';
        const secondImage = document.createElement('img');
        secondImage.src = imageUrls[1];
        secondImage.className = 'w-full h-full object-cover';
        secondImage.alt = 'Right image';
        secondImageDiv.appendChild(secondImage);
        rightHalfDiv.appendChild(secondImageDiv);
      } else {
        const topImageDiv = document.createElement('div');
        topImageDiv.className = 'h-1/2';
        const topImage = document.createElement('img');
        topImage.src = imageUrls[1];
        topImage.className = 'w-full h-full object-cover';
        topImage.alt = 'Right top image';
        topImageDiv.appendChild(topImage);
        rightHalfDiv.appendChild(topImageDiv);

        const bottomImagesDiv = document.createElement('div');
        bottomImagesDiv.className = 'h-1/2 flex gap-1.5';

        if (imageUrls.length === 3) {
          const bottomImageDiv = document.createElement('div');
          bottomImageDiv.className = 'w-full h-full';
          const bottomImage = document.createElement('img');
          bottomImage.src = imageUrls[2];
          bottomImage.className = 'w-full h-full object-cover';
          bottomImage.alt = 'Bottom image';
          bottomImageDiv.appendChild(bottomImage);
          bottomImagesDiv.appendChild(bottomImageDiv);
        } else if (imageUrls.length > 3) {
          const bottomLeftImageDiv = document.createElement('div');
          bottomLeftImageDiv.className = 'w-1/2 h-full';
          const bottomLeftImage = document.createElement('img');
          bottomLeftImage.src = imageUrls[2];
          bottomLeftImage.className = 'w-full h-full object-cover';
          bottomLeftImage.alt = 'Bottom left image';
          bottomLeftImageDiv.appendChild(bottomLeftImage);
          bottomImagesDiv.appendChild(bottomLeftImageDiv);

          const bottomRightImageDiv = document.createElement('div');
          bottomRightImageDiv.className = 'w-1/2 h-full';
          const bottomRightImage = document.createElement('img');
          bottomRightImage.src = imageUrls[3];
          bottomRightImage.className = 'w-full h-full object-cover';
          bottomRightImage.alt = 'Bottom right image';
          bottomRightImageDiv.appendChild(bottomRightImage);
          bottomImagesDiv.appendChild(bottomRightImageDiv);
        }
        rightHalfDiv.appendChild(bottomImagesDiv);
      }
      innerContainer.appendChild(rightHalfDiv);
    }

    flexContainer.appendChild(innerContainer);
    container.appendChild(flexContainer);
    return container;
  }

  private appendHTMLToDOM(html: string) {
    const div = document.createElement('div');
    div.innerHTML = html;
    document.body.appendChild(div);
  }

  removeDynamicImage() {
    const element = document.getElementById('test-img');
    if (element) {
      element.remove();
    }
  }

  private getClasses(index: number, images: string[]): string {
    // Corrected: Added images parameter
    const imageCount = images.slice(0, 4).length; // Limit to 4 images

    if (imageCount === 1) {
      return 'w-full h-full';
    } else if (imageCount === 2) {
      return 'w-1/2 h-full';
    } else if (imageCount === 3 && index === 2) {
      return 'w-full h-full';
    } else if (imageCount === 4 && index > 1) {
      return 'w-1/2 h-full';
    } else if (index === 0 || index === 1) {
      return 'w-1/2 h-full';
    }
    return ''; // Default class if needed
  }

  private createDoc(command: ExportPDFCommandBase) {
    const { jsPDF } = window['jspdf'];

    this.doc = new jsPDF('p', 'mm', 'a4');
    this.drawFooter(command);
    this.pageNumber = 1;
  }

  private addNewPageIfNeeded(
    command: ExportPDFCommandBase,
    thresholdY: number
  ) {
    if (this.y > thresholdY) {
      this.addPage(command);
    }
  }

  private async loadImage(imgUrl: string, type: PDFImageFor) {
    const base64 = await this.loadImageBase64(
      this.imageService.imageSourceUrl(imgUrl)
    );
    if (!base64) {
      console.log(`%cloading ${type} error, ${imgUrl}`, 'color:orange');
    }
    base64 &&
      this.pdfImageBase64.push(new PDFImgDataObject(type, base64, 0, 0));
  }

  private putItemsIntoRows() {
    const itemsToDraw = this.pdfImageBase64.filter(
      (x) => x.imageType === PDFImageFor.item
    );
    let rowGroups = [],
      row = [];
    for (let i = 0; i < itemsToDraw.length; i++) {
      row.push(itemsToDraw[i]);
      if (row.length === 4) {
        rowGroups.push(clone(row));
        row = [];
      }
    }
    rowGroups.push(clone(row));
    rowGroups = rowGroups.filter((x) => x.length);
    console.log('groups', rowGroups);
    return rowGroups;
  }

  // private async loadImageForItems(items: VisitorEvent[] | VisitorPlace[] | (VisitorEvent | VisitorPlace)[], totalItemsCount: number, callback: any, fallbackImage?: string) {
  private async loadImageForItems(
    items: ItineraryAttraction[] | TripAttraction[],
    totalItemsCount: number,
    imageInBannerCount: number,
    callback: any,
    fallbackImage?: string
  ) {
    for (const item of items) {
      let base64 = await this.loadImageBase64(
        this.imageService.imageSourceUrl(item.item.cover?.source)
      );
      if (!base64) {
        console.log(
          `%cImage not load ${item.item.name}: ${item.item.cover.source}`,
          'color:orange'
        );
        if (fallbackImage) {
          if (fallbackImage.startsWith('http')) {
            base64 = await this.loadImageBase64(fallbackImage);
            base64 &&
              this.pdfImageBase64.push(
                new PDFImgDataObject(PDFImageFor.item, base64, 0, 0, item)
              );
          } else if (this.imageService.isBase64Image(fallbackImage)) {
            this.pdfImageBase64.push(
              new PDFImgDataObject(PDFImageFor.item, fallbackImage, 0, 0, item)
            );
          }
        }
      } else {
        this.pdfImageBase64.push(
          new PDFImgDataObject(PDFImageFor.item, base64, 0, 0, item)
        );
      }

      if (callback) {
        let percentage = Math.round(
          ((this.pdfImageBase64.length + imageInBannerCount) /
            (totalItemsCount + imageInBannerCount + 1)) *
            100
        );
        percentage = Math.min(100, percentage);
        callback(percentage);
      }
    }
  }

  private async drawHeader(command: ExportPDFCommandBase) {
    this.drawHeaderBannerRect(command.mainColor, this.bannerHeight);
    if (command.logo) {
      await this.loadImage(command.logo, PDFImageFor.logo);
      await this.drawLogo();
    }
    if (command.pdfIntro) {
      const offsetX = 210 - this.getTextWidth(command.pdfIntro, 15) - 12;
      this.drawText({
        text: command.pdfIntro,
        x: offsetX,
        y: this.bannerHeight / 2 + 2,
        color: '#FFFFFF',
        fontSize: 15,
        fontStyle: 'bold',
      } as DrawTextCommand);
    }
  }

  private drawHeaderBannerRect(
    mainColor: string,
    height: number,
    pageNumber?: number
  ) {
    let color = hexToRgb(mainColor || '#5cbcff');
    this.doc.setFillColor(color.r, color.g, color.b);
    this.doc.rect(0, 0, 210, height, 'F');
    this.doc.setFontSize(12);
    pageNumber && this.doc.text(`Page ${pageNumber}`, 186, height / 2 + 1);
  }

  private async drawLogo() {
    const logoData = this.pdfImageBase64.first(
      (x) => x.imageType === PDFImageFor.logo
    );

    if (logoData && logoData.base64) {
      const targetHeight = 16;
      const size = await this.imageService.getImageSize(logoData.base64);
      const width = this.getRelativeWidth(
        size.width,
        size.height,
        targetHeight
      );
      size.width &&
        this.doc.addImage(
          logoData.base64,
          'PNG',
          8,
          (this.bannerHeight - targetHeight) / 2,
          width,
          targetHeight
        );
    }
  }

  // private async drawTopImageAndTitle(command: ExportPDFCommandBase) {
  //   command.topImage = !command.topImage ? this.imageService.imageSourceUrl(this.defaultTopImages[command.type]) : command.topImage;
  //
  //   await this.loadImage(command.topImage, PDFImageFor.top);
  //   const topHeight = 60, topImageAreaWidth = 120;
  //   // const dateRange = command.type === PDFType.event ? this.getDateRangeText(command as ExportEventPlacePDFCommand) : null;
  //   const dateRange = null;
  //   const text = this.defaultTitleText[command.type].text;
  //   const x = this.defaultTitleText[command.type].x;
  //   const fontSize = this.defaultTitleText[command.type].fontSize || 21;
  //
  //   this.doc.setFillColor(248, 248, 248);
  //   this.doc.rect(0, this.bannerHeight, 210, topHeight, 'F');
  //   this.drawText({
  //     fontSize,
  //     text,
  //     x,
  //     y: dateRange ? 55 : 59,
  //     fontStyle: 'bold'
  //   } as DrawTextCommand);
  //   // this.doc.setFontStyle('normal');
  //   this.doc.setFont(undefined, 'normal');
  //   this.doc.setFontSize(13);
  //
  //   if (dateRange) {
  //     const dateRangeOffsetX = this.getCenteredTextX(dateRange, 210 - topImageAreaWidth);
  //     dateRange && this.doc.text(dateRange, dateRangeOffsetX, 69);
  //   }
  //
  //   const topData = this.pdfImageBase64.first(x => x.imageType === PDFImageFor.top);
  //   const width = topData.width ? this.getRelativeWidth(topData.width, topData.height, topHeight) : topImageAreaWidth;
  //   topData && topData.base64 && this.doc.addImage(topData.base64, 'JPEG', 210 - topImageAreaWidth, this.bannerHeight, width, topHeight);
  //
  //   this.y = this.bannerHeight + topHeight;
  // }
  private async drawTopImageAndTitle(
    command: ExportPDFCommandBase,
    maxHeight = 0
  ) {
    command.topImage = !command.topImage
      ? this.imageService.imageSourceUrl(this.defaultTopImages[command.type])
      : this.imageService.imageSourceUrl(command.topImage);

    // crop banner image, so that the first page will show both the image and the map
    const base64 = await this.imageService.cropImage(
      command.topImage,
      840,
      480
    );

    await this.loadImage(base64, PDFImageFor.top);
    const topData = this.pdfImageBase64.first(
      (x) => x.imageType === PDFImageFor.top
    );
    const size = await this.imageService.getImageSize(topData.base64);
    let height = this.getRelativeHeight(size.width, size.height, 210);
    if (maxHeight) {
      height = Math.min(height, maxHeight);
    }
    size.width && this.doc.addImage(topData.base64, 'JPEG', 0, 0, 210, height);
    this.y = height + 20; //this.bannerHeight + height;
  }

  private calculateTotalAttractionCount(trips: Itinerary[]) {
    let totalItemsCount = 0;
    trips.forEach((trip) => {
      totalItemsCount += trip.attractions.length;
    });
    return totalItemsCount;
  }

  private calculateTotalTripAttractionCount(trip: UserTrip) {
    let totalItemsCount = 0;
    trip.days.forEach((day) => {
      totalItemsCount += day.attractions.length;
    });
    // // each day has a Google map image
    // totalItemsCount += trip.days.length;
    // // add the trip banner image
    // totalItemsCount += 1;
    // // add the createDoc
    // totalItemsCount += 1;
    return totalItemsCount;
  }

  private drawNoStops() {
    const startText = '-- No Stops --';
    const startOffsetX = (210 - this.getTextWidth(startText, 13)) / 2;
    this.drawText({
      x: startOffsetX,
      y: this.y + 5,
      text: startText,
    } as DrawTextCommand);
    this.y += 15;
  }

  private drawDescription(description: string) {
    if (description) {
      // check if there is enough space to draw the description, if not, add a new page before drawing it
      const boxWidth = 170;
      const yIfDrawText = this.drawText(
        {
          text: description,
          x: 25,
          y: this.y + 6,
          width: boxWidth - 20,
          fontSize: 12,
          lineSpacing: 6,
        } as DrawTextCommand,
        true
      );

      if (yIfDrawText > 287) {
        this.addPage(this.command);
      }

      this.y = this.drawText({
        text: description,
        x: 25,
        y: this.y + 6,
        width: boxWidth - 20,
        fontSize: 12,
        lineSpacing: 6,
      } as DrawTextCommand);
    }
  }

  private drawStart() {
    const startText = '-- START --';
    const startOffsetX = (210 - this.getTextWidth(startText, 13)) / 2;
    this.y = this.drawText({
      x: startOffsetX,
      y: this.y < 27 ? this.y + 5 : this.y + 20,
      text: startText,
      fontStyle: 'bold',
    } as DrawTextCommand);
  }

  private drawEnd() {
    const endText = '-- END --';
    const endOffsetX = (210 - this.getTextWidth(endText, 13)) / 2;
    this.drawText({
      x: endOffsetX,
      y: this.y,
      text: endText,
      fontStyle: 'bold',
    } as DrawTextCommand);
    this.y += 5;
  }

  private drawTripAttractions(day: TripDay) {
    let index = 0;
    for (const attr of day.attractions) {
      const item = this.pdfImageBase64.find(
        (x) => x.attraction?.feedId === attr.feedId
      );
      if (!item || !item.base64 || item.imageType !== PDFImageFor.item)
        continue;

      const boxWidth = 170,
        planWidth = 120;

      // draw pre plans
      if ((item.attraction as TripAttraction).prePlans?.length > 0) {
        const prePlans = (item.attraction as TripAttraction).prePlans;
        for (const prePlan of prePlans) {
          if (this.y > 240) {
            this.addPage(this.command);
          }

          const prePlanText = removeHtmlTags(prePlan.content);
          // calculate the height of the plan content first, in order to calculate the y position for the background box
          let y = this.drawText(
            {
              text: `${prePlan.title} · ${prePlan.time || ''}`,
              x: 55,
              y: this.y + 8,
              width: planWidth - 20,
              fontSize: 13,
              lineSpacing: 6,
              fontStyle: 'bold',
            } as DrawTextCommand,
            true
          );

          y = this.drawText(
            {
              text: prePlanText,
              x: 55,
              y: y,
              width: planWidth - 20,
              fontSize: 12,
              lineSpacing: 6,
            } as DrawTextCommand,
            true
          );

          const color = this.getTripPlanColor(prePlan.type);
          const bgColor = hexToRgb(color.bgColor);
          this.doc.setFillColor(bgColor.r, bgColor.g, bgColor.b);
          this.doc.roundedRect(
            48.5,
            this.y,
            planWidth,
            y - this.y - 1,
            4,
            4,
            'F'
          );

          this.y = this.drawText(
            {
              text: `${prePlan.title} · ${prePlan.time || ''}`,
              x: 55,
              y: this.y + 8,
              width: planWidth - 20,
              fontSize: 13,
              lineSpacing: 6,
              color: color.textColor,
              fontStyle: 'bold',
            } as DrawTextCommand,
            false
          );

          this.y = this.drawText(
            {
              text: prePlanText,
              x: 55,
              y: this.y,
              width: planWidth - 20,
              fontSize: 12,
              lineSpacing: 6,
              color: '#878787',
            } as DrawTextCommand,
            false
          );

          this.y += 4;
        }
      }

      let description = removeHtmlTags(item.attraction.item.description);
      if (description.length > 800) {
        description = description.slice(0, 800);
        const lastSpaceIndex = description.lastIndexOf(' ');
        if (lastSpaceIndex !== -1) {
          description = description.slice(0, lastSpaceIndex);
        }
        description += '...';
      }

      if (!!description) {
        // check if there is enough space to draw the description, if not, add a new page
        const yIfDrawText = this.drawText(
          {
            text: description,
            x: 28,
            y: this.y + 5,
            width: boxWidth - 20,
            lineSpacing: 6,
            fontSize: 12,
          } as DrawTextCommand,
          true
        );

        console.log('yIfDrawText ' + item.attraction.item.name, yIfDrawText);

        if (yIfDrawText + 50 > 287) {
          this.addPage(this.command);
        }
      } else {
        if (this.y + 50 > 287) {
          this.addPage(this.command);
        }
      }

      const boxInitY = this.y;

      this.drawImage(
        ConfigService.config.PDF.PDFMapMarkerYellowImage,
        186,
        this.y,
        8,
        12
      );
      // Google static map marker label only supports one digit. So index more than 10, we use marker images.
      // Marker images supports to 14. From 15, we use letters instead.
      const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
      this.drawText({
        x: index + 1 <= 9 || index + 1 >= 15 ? 188.7 : 187.3,
        y: this.y + 6,
        text: (index + 1 <= 14 ? index + 1 : letters[index - 14]).toString(),
      } as DrawTextCommand);

      this.y += 7;
      this.drawItemImage(item, 26, 3);
      const textX = 26 + 40 + 6;
      const textWidth = boxWidth - textX;
      const isTitleOverflow = this.isTextOverflow(
        item.attraction.item.name,
        15,
        textWidth
      );
      const address =
        item.attraction.item.address.address || item.attraction.item.address;
      const isAddressOverflow = this.isTextOverflow(
        address.toString(),
        13,
        textWidth
      );
      if (isTitleOverflow && isAddressOverflow) {
        this.y -= 2.5;
      } else if (!isTitleOverflow && !isAddressOverflow) {
        this.y += 2.5;
      }
      this.drawText({
        text: item.attraction.item.name,
        x: textX,
        y: this.y + 6,
        maxLines: 2,
        width: textWidth + 4,
        fontSize: 15,
        fontStyle: 'bold',
        lineSpacing: 6,
      } as DrawTextCommand);
      if (item.attraction.item['isExpired']) {
        const y = this.y + (isTitleOverflow ? 19 : 13);
        const color = hexToRgb('#fd7c7c');
        this.doc.setFillColor(color.r, color.g, color.b);
        this.doc.roundedRect(textX, y - 4.5, 13, 5, 1, 1, 'F');
        this.drawText({
          text: 'Expired',
          x: textX + 2,
          y: y - 1,
          width: textWidth,
          fontSize: 8,
          color: '#fff',
        } as DrawTextCommand);
      }
      this.drawText({
        text:
          item.attraction.item['timeOneLine'] ||
          item.attraction.item['categoryText'],
        x: textX + (item.attraction.item['isExpired'] ? 14 : 0),
        y: this.y + (isTitleOverflow ? 19 : 13),
        maxLines: 1,
        width: textWidth,
      } as DrawTextCommand);
      this.y = this.drawText({
        text: address,
        x: textX,
        y: this.y + (isTitleOverflow ? 26 : 20),
        maxLines: 2,
        width: textWidth,
        lineSpacing: 6,
      } as DrawTextCommand);
      if (!!description) {
        this.y = this.drawText({
          text: description,
          x: 28,
          y: this.y + 5,
          // maxLines: 2,
          width: boxWidth - 20,
          lineSpacing: 6,
          fontSize: 12,
        } as DrawTextCommand);
      } else {
        this.y += 3;
      }

      // draw attraction border
      this.doc.setLineWidth(0.1);
      let color = hexToRgb('#bfbfbf');
      this.doc.setDrawColor(color.r, color.g, color.b);
      this.doc.roundedRect(
        20,
        boxInitY + 3,
        boxWidth,
        this.y - boxInitY - 2,
        3,
        3,
        'S'
      );

      // draw post plans
      if ((item.attraction as TripAttraction).postPlans?.length > 0) {
        const postPlans = (item.attraction as TripAttraction).postPlans;
        for (const postPlan of postPlans) {
          if (this.y > 230) {
            this.addPage(this.command);
          }

          const prePlanText = removeHtmlTags(postPlan.content);
          // calculate the height of the plan content first, in order to calculate the y position for the background box
          let y = this.drawText(
            {
              text: `${postPlan.title} · ${postPlan.time || ''}`,
              x: 55,
              y: this.y + 8,
              width: planWidth - 20,
              fontSize: 13,
              lineSpacing: 6,
              fontStyle: 'bold',
            } as DrawTextCommand,
            true
          );

          y = this.drawText(
            {
              text: prePlanText,
              x: 55,
              y: y,
              width: planWidth - 20,
              fontSize: 12,
              lineSpacing: 6,
            } as DrawTextCommand,
            true
          );

          const color = this.getTripPlanColor(postPlan.type);
          const bgColor = hexToRgb(color.bgColor);
          this.doc.setFillColor(bgColor.r, bgColor.g, bgColor.b);
          this.doc.roundedRect(
            48.5,
            this.y,
            planWidth,
            y - this.y - 1,
            4,
            4,
            'F'
          );

          this.y = this.drawText(
            {
              text: `${postPlan.title} · ${postPlan.time || ''}`,
              x: 55,
              y: this.y + 8,
              width: planWidth - 20,
              fontSize: 13,
              lineSpacing: 6,
              color: color.textColor,
              fontStyle: 'bold',
            } as DrawTextCommand,
            false
          );

          this.y = this.drawText(
            {
              text: prePlanText,
              x: 55,
              y: this.y,
              width: planWidth - 20,
              fontSize: 12,
              lineSpacing: 6,
              color: '#878787',
            } as DrawTextCommand,
            false
          );

          this.y += 4;
        }
      }

      index++;
      this.y += 9;
    }
  }

  private getTripPlanColor(type: string) {
    return TripPlannerService.planTypes.find((x) => x.type === type);
  }

  private drawItineraryAttractions(
    command: ExportItineraryPDFCommand,
    directionData: DirectionData
  ) {
    let index = 0;
    for (const item of this.pdfImageBase64) {
      if (!item.base64) continue;

      const boxWidth = 170;

      const preDesc = removeHtmlTags(
        (item.attraction as ItineraryAttraction).preDescription
      );
      if (!!preDesc) {
        // check if there is enough space to draw the pre description, if not, add a new page before drawing it
        const yIfDrawText = this.drawText(
          {
            text: preDesc,
            x: 28,
            y: this.y + 16,
            width: boxWidth - 20,
            fontSize: 12,
            lineSpacing: 6,
          } as DrawTextCommand,
          true
        );

        if (yIfDrawText > 287) {
          this.addPage(this.command);
        }

        this.y = this.drawText({
          text: preDesc,
          x: 28,
          y: this.y + 16,
          width: boxWidth - 20,
          fontSize: 12,
          lineSpacing: 6,
        } as DrawTextCommand);
      }

      let description = removeHtmlTags(item.attraction.item.description);
      if (description.length > 800) {
        description = description.slice(0, 800);
        const lastSpaceIndex = description.lastIndexOf(' ');
        if (lastSpaceIndex !== -1) {
          description = description.slice(0, lastSpaceIndex);
        }
        description += '...';
      }

      if (!!description) {
        // check if there is enough space to draw the description, if not, add a new page
        const yIfDrawText = this.drawText(
          {
            text: description,
            x: 28,
            y: this.y + 5,
            width: boxWidth - 20,
            lineSpacing: 6,
            fontSize: 12,
          } as DrawTextCommand,
          true
        );

        console.log('yIfDrawText ' + item.attraction.item.name, yIfDrawText);

        if (yIfDrawText + 50 > 287) {
          this.addPage(this.command);
        }
      } else {
        if (this.y + 50 > 287) {
          this.addPage(this.command);
        }
      }

      const boxInitY = this.y;

      this.drawImage(
        ConfigService.config.PDF.PDFMapMarkerYellowImage,
        186,
        this.y,
        8,
        12
      );
      this.drawText({
        x: 188.7,
        y: this.y + 6,
        text: (index + 1).toString(),
      } as DrawTextCommand);

      this.y += 7;
      this.drawItemImage(item, 26, 3);
      const textX = 26 + 40 + 6;
      const textWidth = boxWidth - textX;
      const isTitleOverflow = this.isTextOverflow(
        item.attraction.item.name,
        15,
        textWidth
      );
      const address =
        item.attraction.item.address.address || item.attraction.item.address;
      const isAddressOverflow = this.isTextOverflow(
        address.toString(),
        13,
        textWidth
      );
      if (isTitleOverflow && isAddressOverflow) {
        this.y -= 2.5;
      } else if (!isTitleOverflow && !isAddressOverflow) {
        this.y += 2.5;
      }
      // let title = item.attraction.item.name.length > 35 ? item.attraction.item.name.substring(0, 35) + '...' : item.attraction.item.name;
      this.drawText({
        text: item.attraction.item.name,
        x: textX,
        y: this.y + 6,
        maxLines: 2,
        width: textWidth + 4,
        fontSize: 15,
        fontStyle: 'bold',
        lineSpacing: 6,
      } as DrawTextCommand);
      this.drawText({
        text:
          item.attraction.item['timeOneLine'] ||
          item.attraction.item['categoryText'],
        x: textX,
        y: this.y + (isTitleOverflow ? 19 : 13),
        maxLines: 1,
        width: textWidth,
      } as DrawTextCommand);
      this.y = this.drawText({
        text: address,
        x: textX,
        y: this.y + (isTitleOverflow ? 26 : 20),
        maxLines: 2,
        width: textWidth,
        lineSpacing: 6,
      } as DrawTextCommand);
      if (!!description) {
        this.y = this.drawText({
          text: description,
          x: 28,
          y: this.y + 5,
          // maxLines: 2,
          width: boxWidth - 20,
          lineSpacing: 6,
          fontSize: 12,
        } as DrawTextCommand);
      } else {
        this.y += 3;
      }

      this.doc.setLineWidth(0.1);
      let color = hexToRgb('#bfbfbf');
      this.doc.setDrawColor(color.r, color.g, color.b);
      this.doc.roundedRect(
        20,
        boxInitY + 3,
        boxWidth,
        this.y - boxInitY - 2,
        3,
        3,
        'S'
      );

      const postDesc = removeHtmlTags(
        (item.attraction as ItineraryAttraction).postDescription
      );
      if (!!postDesc) {
        // check if there is enough space to draw the post description, if not, add a new page before drawing it
        const yIfDrawText = this.drawText(
          {
            text: postDesc,
            x: 28,
            y: this.y + 13,
            width: boxWidth - 20,
            fontSize: 12,
            lineSpacing: 6,
          } as DrawTextCommand,
          true
        );

        if (yIfDrawText > 287) {
          this.addPage(this.command);
        }

        this.y = this.drawText({
          text: postDesc,
          x: 28,
          y: this.y + 13,
          width: boxWidth - 20,
          fontSize: 12,
          lineSpacing: 6,
        } as DrawTextCommand);
      }

      this.y += 15;

      let totalDistanceText = '';
      if (!directionData) {
        totalDistanceText = `-- to --`;
      } else {
        if (directionData.legs && index < directionData.legs.length) {
          const totalDistanceKM =
            Math.round(directionData.legs[index].distance.value / 1000) + ' km';
          totalDistanceText = `-- ${directionData.legs[index].distance.text} / ${totalDistanceKM} to --`;
        }
      }
      const totalDistanceTextWidth = this.getTextWidth(totalDistanceText, 13);
      const offsetX = (210 - totalDistanceTextWidth) / 2;

      if (index !== this.pdfImageBase64.length - 1) {
        this.addNewPageIfNeeded(command, 250);
        this.y = this.drawText({
          x: offsetX,
          y: this.y,
          text: totalDistanceText,
        } as DrawTextCommand);
        this.y += 3;
        this.addNewPageIfNeeded(command, 250);
      }

      index++;
    }
  }

  private async drawDirectionMap(
    trip: Itinerary | TripDay,
    appKey: string
  ): Promise<DirectionData> {
    const directionData = await this.getGoogleMapImageUrlAndLegs(
      trip.attractions,
      appKey
    );
    console.log('directionData', directionData);
    if (!directionData) return null;

    // directionData.mapImageUrl = `https://maps.googleapis.com/maps/api/staticmap?size=640x310&language=en&maptype=roadmap&key=AIzaSyBTK-7XADsofCdPvDWP4Rh2QzSK6t-POaw&markers=color:0xEBCF72%7Clabel:1%7C32.0732043,-81.0918691&markers=color:0xEBCF72%7Clabel:2%7C32.0779551,-81.092299&markers=color:0xEBCF72%7Clabel:3%7C32.0675356,-81.09623839999999&markers=color:0xEBCF72%7Clabel:4%7C32.0821632,-81.0921515&markers=color:0xEBCF72%7Clabel:5%7C32.0685205,-81.0919086`;
    // directionData.mapImageUrl = `https%3A%2F%2Fmaps.googleapis.com%2Fmaps%2Fapi%2Fstaticmap%3Fsize%3D640x310%26language%3Den%26maptype%3Droadmap%26path%3Dcolor%3A0x1E90FFCC%257Cweight%3A7IowaMinneapolis%257Cgeodesic%3Atrue%257Cenc%3As%7DxbE~c%7DmNl%40%7DDv%40gFRsARq%40Z%7BBRgBJ%7DAn%40oDZcB%5C_BhA_E%60AmD%60%40u%40V%5Dd%40_%40PKVCVNRe%40%5Cg%40h%40e%40PKl%40SfD_AlAYxA%5DTCt%40%40n%40HlA%5CZFRqAJk%40jCcPbAcIHy%40f%40gDZuB%7B%40S_AUcAc%40Yc%40Qm%40Eg%40D%5DNa%40PW%5CSZI%60%40%3Fb%40DpGj%40vCVtATrA%5CbBt%40x%40d%40t%40l%40r%40n%40xCrCdAz%40v%40b%40rAf%40%7C%40RrALvA%40lAI%5CGj%40M%7C%40YlAm%40v%40k%40bAaAx%40kAd%40_AzBcFj%40kAj%40w%40l%40s%40t%40o%40x%40e%40%5CQp%40WlAYrAMpA%3FbDJnAJlAP%7CBh%40%60Dv%40zAXp%40FhBDzAGx%40Kl%40MlA%5Dh%40Sx%40c%40v%40g%40r%40k%40hAaAxCgCxBaBpAu%40zAs%40tAi%40x%40WzA_%40~%40SbBUnAKxAGbAA%7C%40%3F%7CAFx%40FrBV~FbAdCb%40p%40NhCx%40bEdBrCnAlAp%40bAv%40dAdAhAzAl%40fArApCjDlHpB%7CDrCbG%7CAfDZv%40%5EnAXxAh%40pDLz%40CZ%60%40jEZrCLxBOrE%3Fz%40Fb%40HTT%5EPNRL%5EJv%40P%5ELU%60BeBxKkBhLMt%40QlAl%40LhAZZF~Cx%40tDfA~F%7CAbFlAzCx%40PsAj%40eDRWHEEMGUFk%40V%7DAIGOCC%3F%26key%3DAIzaSyDNC_nOxgf5SRiTZrkOGhr8UdyBghwmfY0%26markers%3Dcolor%3A0xEBCF72%257Clabel%3A1%257C32.0815354%2C-81.0914177%26markers%3Dcolor%3A0xEBCF72%257Clabel%3A2%257C32.0303424%2C-81.089434`;
    const base64 = await this.loadImageBase64(
      encodeURIComponent(directionData.mapImageUrl)
    );
    const y = this.y;
    base64 && (this.y = this.drawImage(base64, 25, this.y, 160, 80));

    // in order to make the map image rounded corner and has a gray border, draw 2 rounded rectangles cover the image
    this.doc.setLineWidth(3);
    this.doc.setDrawColor(255, 255, 255);
    this.doc.roundedRect(23.5, y, 163, 81.4, 4, 4, 'S');
    let color = hexToRgb('#898989');
    this.doc.setDrawColor(color.r, color.g, color.b);
    this.doc.setLineWidth(0.1);
    this.doc.roundedRect(25, y + 1.3, 160, 79, 3, 3, 'S');

    return directionData;
  }

  private drawTripInfo(
    trip: Itinerary | TripDay,
    directionData: DirectionData
  ) {
    if (!trip.attractions || trip.attractions.length <= 1) {
      return;
    }

    let totalDistance = trip['distance'] || 0,
      totalTravelTime = trip['travelTime'] || 0;
    if (!trip['travelTime'] || !trip['distance']) {
      if (directionData && directionData.legs) {
        directionData.legs.forEach((leg) => {
          totalDistance += leg.distance.value;
          totalTravelTime += leg.duration.value;
        });
      }
    }

    const totalTravelTimeText =
      this.timeService.convertToTravelTimeText(totalTravelTime);
    const totalDistanceMiles = Math.round(totalDistance / 1610) + ' miles';
    const totalDistanceKM = Math.round(totalDistance / 1000) + ' km';

    const travelTimeWidth = this.getTextWidth(totalTravelTimeText, 13);
    const distanceWidth = this.getTextWidth(
      `${totalDistanceMiles} / ${totalDistanceKM}`,
      13
    );
    const stopsWidth = this.getTextWidth(
      trip.attractions.length.toString(),
      13
    );
    const iconAndSpacingWidth = 3 + 3 + 6 + 22;
    const tripInfoWidth =
      travelTimeWidth + distanceWidth + stopsWidth + iconAndSpacingWidth;

    const offsetX = (210 - tripInfoWidth) / 2;

    this.drawImage(
      ConfigService.config.PDF.PDFMapMarkerImage,
      offsetX,
      this.y + 3,
      3,
      4
    );
    this.drawText({
      text: trip.attractions.length.toString(),
      x: offsetX + 6,
      y: this.y + 7,
    } as DrawTextCommand);

    this.drawImage(
      ConfigService.config.PDF.PDFTimerImage,
      offsetX + 16,
      this.y + 2,
      6,
      6
    );
    this.drawText({
      text: totalTravelTimeText,
      x: offsetX + 23,
      y: this.y + 7,
    } as DrawTextCommand);

    this.drawImage(
      ConfigService.config.PDF.PDFMileImage,
      offsetX + travelTimeWidth + 30,
      this.y + 3.5,
      3,
      3
    );
    this.drawText({
      text: `${totalDistanceMiles} / ${totalDistanceKM}`,
      x: offsetX + travelTimeWidth + 37,
      y: this.y + 7,
    } as DrawTextCommand);

    this.y += 20;
  }

  private drawTripTitle(tripTitle: string) {
    if (tripTitle) {
      let width = 210,
        offsetX = 0;
      if (this.isTextOverflow(tripTitle, 21)) {
        width = 180;
        offsetX = (210 - width) / 2;
      } else {
        offsetX = this.getCenteredTextX(tripTitle, 210);
      }

      this.y = this.drawText({
        text: tripTitle,
        x: offsetX,
        y: this.y,
        fontSize: 21,
        lineSpacing: 9,
        maxLines: 2,
        width: width,
      } as DrawTextCommand);
    }
  }

  // private getDateRangeText(command: ExportEventPlacePDFCommand) {
  //   let text = '';
  //   if (command.from && !command.to) {
  //     text = `AFTER ` + this.timeService.toShortDateFormat(command.from);
  //   } else if (!command.from && command.to) {
  //     text = `BEFORE ` + this.timeService.toShortDateFormat(command.to);
  //   } else if (command.from && command.to) {
  //     text = `${this.timeService.toShortDateFormat(command.from)} - ${this.timeService.toShortDateFormat(command.to)}`;
  //   }
  //   return text;
  // }

  private getRelativeWidth(
    imgWidth: number,
    imgHeight: number,
    targetHeight: number
  ) {
    return (imgWidth * targetHeight) / imgHeight;
  }

  private getRelativeHeight(
    imgWidth: number,
    imgHeight: number,
    targetWidth: number
  ) {
    return (imgHeight * targetWidth) / imgWidth;
  }

  private getDrawItemInfoFunc(code: string) {
    // event: ['image', 'name', 'time', 'organizer', 'address', 'more'],
    //     place: ['image', 'name', 'category', 'rating', 'address', 'more']
    const funcList = {
      image: this.drawItemImage,
      name: this.drawName,
      time: this.drawTime,
      organizer: this.drawOrganizer,
      address: this.drawAddress,
      category: this.drawCategory,
      rating: this.drawRating,
      more: this.drawMoreInfoBtn,
    };
    return funcList[code];
  }

  private drawItemImage(item, x: number, borderRadius?: number) {
    const lastY = this.drawImage(item.base64, x, this.y, 40, 28, borderRadius);
    this.maxY = lastY + 6;
  }

  private drawName(item, x: number) {
    const rowY = this.drawText({
      text: item.item.name,
      x,
      y: this.y,
      fontSize: 8,
      fontStyle: 'bold',
      lineSpacing: 4,
      maxLines: 3,
      width: 40,
    } as DrawTextCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private drawTime(item, x: number) {
    this.drawImage(
      ConfigService.config.PDF.PDFTimerImage,
      x - 1,
      this.y - 2,
      6,
      6
    );
    const rowY = this.drawText({
      text: item.item.startTimeFormatted,
      x: x + 6,
      y: this.y + 2,
      fontSize: 8,
      fontStyle: 'normal',
      lineSpacing: 4,
      maxLines: 1,
      width: 34,
    } as DrawTextCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private drawOrganizer(item, x: number) {
    this.drawImage(
      ConfigService.config.PDF.PDFOwnerImage,
      x + 0.5,
      this.y - 1,
      3,
      3
    );
    const rowY = this.drawText({
      text: item.item.owner.name,
      x: x + 6,
      y: this.y + 2,
      fontSize: 8,
      fontStyle: 'normal',
      lineSpacing: 4,
      maxLines: 2,
      width: 34,
    } as DrawTextCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private drawAddress(item, x: number) {
    this.drawImage(
      ConfigService.config.PDF.PDFMapMarkerImage,
      x + 0.5,
      this.y - 1.5,
      3,
      4
    );
    const rowY = this.drawText({
      text: item.item['isOnlineEvent']
        ? '(Online event)'
        : item.item.address.address || item.item.address,
      x: x + 6,
      y: this.y + 2,
      fontSize: 8,
      fontStyle: 'normal',
      lineSpacing: 4,
      maxLines: 3,
      width: 34,
    } as DrawTextCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private drawRating(item, x: number) {
    if (!item.item['rating']) {
      // in order not to have an empty row
      item.item['rating'] = 5;
    }

    this.drawImage(
      ConfigService.config.PDF.PDFStarImage,
      x,
      this.y - 1,
      4,
      3.5
    );
    const rowY = this.drawText({
      text: item.item['rating'].toString(),
      x: x + 6,
      y: this.y + 2,
      fontSize: 8,
      fontStyle: 'normal',
      lineSpacing: 4,
      maxLines: 3,
      width: 34,
    } as DrawTextCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private drawCategory(item, x: number) {
    this.drawImage(
      ConfigService.config.PDF.PDFCategoryImage,
      x + 0.5,
      this.y - 0.5,
      3,
      3
    );
    const category = this.processCategory(item.item.categoryList);
    const rowY = this.drawText({
      text: category,
      x: x + 6,
      y: this.y + 2,
      fontSize: 8,
      fontStyle: 'normal',
      lineSpacing: 4,
      maxLines: 3,
      width: 34,
    } as DrawTextCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private processCategory(categoryList: string[]) {
    const newCategoryList = categoryList.map((y) => {
      let temp = y
        .replace(/(_)+/g, ' ')
        .split(' ')
        .map((z) => (z === 'of' ? z : upperFirstLetter(z)));
      return temp.join(' ');
    });
    return newCategoryList.join(', ');
  }

  private drawMoreInfoBtn(item, x: number) {
    const rowY = this.drawFlatButton({
      btnText: 'MORE INFORMATION',
      x,
      y: this.y,
      width: 40,
      height: 8.5,
      link: item.item['eventLink'] || item.item['hostLink'],
      isTextAtPageCenter: false,
    } as DrawFlatLinkButtonCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private drawImage(
    imgBase64: string,
    x: number,
    y: number,
    width: number,
    height: number,
    borderRadius?: number
  ): number {
    try {
      this.doc.addImage(imgBase64, 'PNG', x, y, width, height);
    } catch (e) {
      console.log(
        `%cDraw image error`,
        'color:rgba(133,108,217,1);font-size:30px'
      );
      this.doc.addImage(this.errorFallbackImage, 'PNG', x, y, width, height);
    }

    if (borderRadius) {
      this.doc.setLineWidth(3);
      this.doc.setDrawColor(255, 255, 255);
      this.doc.roundedRect(
        x,
        y,
        width,
        height,
        borderRadius,
        borderRadius,
        'S'
      );
    }

    return y + height;
  }

  private drawText(command: DrawTextCommand, isOnlyGetY = false): number {
    this.doc.setFontSize(command.fontSize || 13);
    // this.doc.setFontStyle(command.fontStyle || 'normal');
    this.doc.setFont(undefined, command.fontStyle || 'normal');
    const rgb = hexToRgb(command.color || this.textDefaultColor);
    this.doc.setTextColor(rgb.r, rgb.g, rgb.b);

    // if (isNullOrUndefined(thresholdY)) {
    //   return this.splitAndPrintText(command.text, command.width, command.maxLines, command.x, command.y, command.lineSpacing || 4, false);
    // }
    //
    // let lastY = this.splitAndPrintText(command.text, command.width, command.maxLines, command.x, command.y, command.lineSpacing || 4, true);
    // if (lastY < thresholdY) {
    //   lastY = this.splitAndPrintText(command.text, command.width, command.maxLines, command.x, command.y, command.lineSpacing || 4, false);
    // } else {
    //   this.addPage(this.command);
    //   lastY = this.y;
    // }

    if (isOnlyGetY) {
      return this.splitAndPrintText(
        command.text,
        command.width,
        command.maxLines,
        command.x,
        command.y,
        command.lineSpacing || 4,
        true
      );
    }
    return this.splitAndPrintText(
      command.text,
      command.width,
      command.maxLines,
      command.x,
      command.y,
      command.lineSpacing || 4,
      false
    );
  }

  private addPage(command: ExportPDFCommandBase) {
    this.doc.addPage();
    this.drawFooter(command);
    this.pageNumber++;
    this.drawHeaderBannerRect(command.mainColor, 12, this.pageNumber);
    this.y = 26;
  }

  private drawIconAndText(command: DrawIconAndTextCommand): number {
    this.doc.addImage(
      command.iconBase64,
      'JPEG',
      command.iconX,
      command.iconY,
      command.iconWidth,
      command.iconHeight
    );
    return this.drawText(command as DrawTextCommand);
  }

  private drawFlatButton(command: DrawFlatLinkButtonCommand): number {
    const color = hexToRgb(command.btnColor || '#5cbcff');
    this.doc.setFillColor(color.r, color.g, color.b);
    this.doc.rect(command.x, command.y, command.width, command.height, 'F');
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(command.fontSize || 8);
    // this.doc.setFontStyle(command.fontStyle || 'bold');
    this.doc.setFont(undefined, command.fontStyle || 'bold');
    const offsetX = command.isTextAtPageCenter
      ? this.getCenteredTextX(command.btnText)
      : command.x + 5;
    const offsetY = command.height > 12 ? command.y + 9 : command.y + 5;
    this.doc.textWithLink(command.btnText, offsetX, offsetY, {
      url: command.link,
    });
    return command.y + 5;
  }

  private drawFooter(command: ExportPDFCommandBase) {
    this.drawTripFooter(command as ExportItineraryPDFCommand);
    // if (command.type === PDFType.event || command.type === PDFType.place) {
    //   this.drawEventPlaceFooter(command as ExportEventPlacePDFCommand);
    // } else {
    //   this.drawTripFooter(command as ExportTripPDFCommand);
    // }
  }

  //
  // private drawEventPlaceFooter(command: ExportEventPlacePDFCommand) {
  //   const eventLink = !command.eventCalendarURL ? `${config.serverDomain}${command.hubName}/events` : command.eventCalendarURL;
  //   const placeLink = !command.placeCalendarURL ? `${config.serverDomain}${command.hubName}/places/all` : command.placeCalendarURL;
  //   const text = command.type === PDFType.event ? `Visit our events calendar at ${eventLink}` : `Visit more places at ${placeLink}`;
  //   const height = 16;
  //   this.drawFlatButton({
  //     btnText: text,
  //     link: command.type === PDFType.event ? eventLink : placeLink,
  //     height,
  //     x: 0,
  //     y: 297 - height,
  //     fontSize: 14,
  //     width: 210,
  //     btnColor: command.mainColor,
  //     isTextAtPageCenter: true
  //   } as DrawFlatLinkButtonCommand);
  // }
  //
  private drawTripFooter(command: ExportPDFCommandBase) {
    let tourLink =
      command['moreToursURL'] ||
      window.location.href ||
      `${ConfigService.config.domainV3}${command.hubName}/itineraries`;
    // let text =
    //   command.type === PDFType.tour
    //     ? `Visit more itineraries at ${tourLink}`
    //     : `Visit this trip at ${tourLink}`;
    let text =
      command.type === PDFType.tour
        ? `VISIT THIS ITINERARY HERE`
        : `VISIT THIS TRIP HERE`;
    text = text.length > 90 ? text.substring(0, 87) + '...' : text;
    const height = 16;
    this.drawFlatButton({
      btnText: text,
      link: tourLink,
      height,
      x: 0,
      y: 297 - height,
      fontSize: 13,
      width: 210,
      btnColor: command.mainColor,
      isTextAtPageCenter: true,
    } as DrawFlatLinkButtonCommand);
  }

  private savePDF(command: ExportPDFCommandBase) {
    this.doc.save(
      `${
        command.type === PDFType.event
          ? 'Events'
          : command.type === PDFType.place
          ? 'Places'
          : command.type === PDFType.tour
          ? ' Itineraries'
          : 'Trips'
      }.pdf`
    );
    this.pdfImageBase64 = [];
    command.event && command.event.complete && command.event.complete();
    command.completeCallback && command.completeCallback();
  }

  private isTextOverflow(
    text: string,
    fontSize?: number,
    parentWidth?: number
  ) {
    fontSize && this.doc.setFontSize(fontSize);
    const textWidth = this.getTextWidth(text);
    const pageWidth =
      parentWidth ||
      (this.doc.internal.pageSize.width
        ? this.doc.internal.pageSize.width
        : 210);

    return textWidth > pageWidth;
  }

  private getCenteredTextX(text: string, parentWidth?: number) {
    const textWidth = this.getTextWidth(text);
    let pageWidth =
      parentWidth ||
      (this.doc.internal.pageSize.width
        ? this.doc.internal.pageSize.width
        : 210);
    let textOffset = (pageWidth - textWidth) / 2;
    return textOffset;
  }

  private getTextWidth(text: string, fontSize?: number) {
    fontSize && this.doc.setFontSize(fontSize);
    return (
      (this.doc.getStringUnitWidth(text) * this.doc.internal.getFontSize()) /
      this.doc.internal.scaleFactor
    );
  }

  async loadImageBase64(imgUrl: string): Promise<string> {
    if (imgUrl.startsWith('data:')) {
      return imgUrl;
    }
    // image on the prod server cannot be loaded from dev server (under testing now), so load it from client side here
    if (imgUrl.indexOf(ConfigService.config.domainV2) >= 0) {
      const base64 = await this.imageService.getBase64ImageByUrl(imgUrl);
      return base64 ? base64.base64 : null;
    }
    // images from Amazon S3 has CORS problems (have already set in the Amazon dashboard, but still not work), so here is to get the image base64 from server side
    const base64 = await this.getBase64FromServer(imgUrl).catch(async (e) => {
      throw e;
    });
    return base64;

    // return new Promise<string>((resolve) => {
    //     this.imageService.getBase64ImageByUrl(imgUrl).then(base64 => {
    //         resolve(base64 ? base64.base64 : null);
    //     }).catch(async e => {
    //         // images from Amazon S3 has CORS problems (have already set in the Amazon dashboard, but still not work), so here is to get the image base64 from server side
    //         const base64 = await this.getBase64FromServer(imgUrl);
    //         resolve(base64);
    //     });
    // })
  }

  private getBase64FromServer(imgUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!imgUrl) {
        resolve(null);
        return;
      }

      const url = this.imageService.imageSourceUrl(imgUrl);
      if (this.imageService.isBase64Image(url)) {
        resolve(url);
        return;
      }

      // images from Amazon S3 has CORS problems (have already set in the Amazon dashboard, but still not work), so here is to get the image base64 from server side
      if (url && url.startsWith('http')) {
        this.visitorService.getImageBase64(url).subscribe((data) => {
          let base64 = data?.succeed ? data.base64 : null;
          resolve(base64);
        });
      } else {
        reject('please check image url.');
      }
    });
  }

  private splitAndPrintText(
    text: string,
    width: number,
    maxLines: number,
    x: number,
    y: number,
    lineSpacing: number,
    isOnlyGetY = false
  ): number {
    let sections = this.doc.splitTextToSize(
      this.replaceSpecialChar(text, false),
      width || 210
    );
    if (sections.length > maxLines) {
      sections = sections.slice(0, maxLines);
      sections[maxLines - 1] = sections[maxLines - 1] + '...';
    }
    for (let section of sections) {
      !isOnlyGetY && this.doc.text(section, x, y);
      y += lineSpacing;
    }
    return y;
  }

  private replaceSpecialChar(description: string, isPrintVersion: boolean) {
    if (!description) return '';

    if (!isPrintVersion) {
      description = description.replace(/(\n)+|(\r\n)+/g, '');
      description = description.replace(/(<br>)+/g, '');
      description = description.replace(/(<br\/>)+/g, '');
      description = description.replace(/(<p>)+/g, '');
      description = description.replace(/(<\/p>)+/g, '');
    } else {
      description = description.replace(/(\n)+|(\r\n)+/g, '<br><br>');
      description = description.replace(/(<br\/>)+/g, '<br>');
      description = description.replace(/(<p>)+/g, '<br><br>');
      description = description.replace(/(<\/p>)+/g, '<br><br>');
    }

    description = description.replace(/(<i>)+/g, '');
    description = description.replace(/(<\/i>)+/g, '');
    description = description.replace(/(<b>)+/g, '');
    description = description.replace(/(<\/b>)+/g, '');
    description = description.replace(/(<strong>)+/g, '');
    description = description.replace(/(<\/strong>)+/g, '');
    description = description.replace(/(<a.*?>)+/g, '');
    description = description.replace(/(<\/a>)+/g, '');
    description = description.replace(/(&#39;)+/g, "'");
    description = description.replace(/(&quot;)+/g, '"');
    description = description.replace(/(&gt;)+/g, '>');
    description = description.replace(/(&lt;)+/g, '<');

    description = this.getAllowedText(description);

    // description = description.replace(/(’)+/g, "'");
    // description = description.replace(/(’)+/g, "'");
    // description = description.replace(/(‘)+/g, "'");
    // description = description.replace(/(”)+/g, "\"");
    // description = description.replace(/(“)+/g, "\"");
    // description = description.replace(/(?)+/g, "");
    // description = description.replace(/(–)+/g, "-");
    // description = description.replace(/(—)+/g, "-");
    // description = description.replace(/(…)+/g, "...");
    // description = description.replace(/(?)+/g, "");
    // description = description.replace(/(?)+/g, "");
    // description = description.replace(/(?)+/g, "");
    // description = description.replace(/\s+/g, " ");
    // description = description.replace(/(\. ?)+/g, ". ");
    // description = description.replace(/(\! ?)+/g, ". ");

    if (description.startsWith(' ')) {
      description = description.substr(1, description.length - 1);
    }

    return description;
  }

  private getAllowedText(section): string {
    if (!this.isAllSymbolAllowed(section)) {
      console.log(`${section}: contains invalid symbols`);
      let newText = '';
      for (let symbol of section.toString().split('')) {
        if (this.isAllSymbolAllowed(symbol)) {
          newText += symbol;
        }
      }
      console.log(`newText: ${newText}`);
      section = newText;
    }
    return section;
  }

  private isAllSymbolAllowed(str) {
    if (!str) {
      return true;
    }
    return new RegExp(
      '^[-+A-Za-z_0-9~é`!@#$%^&*·()\'’".:,/\\\\;<>{}\\[\\]|=? ]+$'
    ).test(str);
  }

  private replaceBr(description) {
    while (description.startsWith('<br>') || description.endsWith('<br>')) {
      description = this.removeBr(description);
    }
    return description;
  }

  private removeBr(description) {
    if (description.startsWith('<br>')) {
      description = description.substr(4, description.length - 4);
    }
    if (description.endsWith('<br>')) {
      description = description.substr(0, description.length - 4);
    }
    return description;
  }

  private getGoogleMapImageUrlAndLegs(
    items: ItineraryAttraction[] | TripAttraction[],
    appKey: string
  ): Promise<DirectionData> {
    appKey = appKey || 'AIzaSyBTK-7XADsofCdPvDWP4Rh2QzSK6t-POaw';

    return new Promise<any>((resolve, reject) => {
      let mapImageUrl = '';
      if (!items.length) {
        resolve(null);
      } else if (items.length === 1) {
        let lat = items[0].item.address.lat || items[0].item.lat;
        mapImageUrl = ConfigService.config.google.oneRealPhoto.replace(
          '${lat}',
          lat.toString()
        );
        let lng = items[0].item.address.lng || items[0].item.lng;
        mapImageUrl = mapImageUrl.replace('${lng}', lng.toString());
        mapImageUrl = mapImageUrl.replace('${appKey}', appKey);
        resolve({ mapImageUrl, legs: null });
      } else {
        const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
        this.googleMapService.getDirection(
          items,
          TravelMode.DRIVING,
          (res) => {
            if (!res.overview_polyline || !res.legs) {
              return resolve({ mapImageUrl: null, legs: null });
            }
            let markers = [];
            items.forEach((item, i) => {
              // Google static map only support one digital marker label
              if (i < 9) {
                markers.push(
                  `markers=color:0xEBCF72%7Clabel:${i + 1}%7C${
                    item.item.address.lat || item.item.lat
                  },${item.item.address.lng || item.item.lng}`
                );
              }
              // for 10 - 14, use digital marker icon image
              if (i >= 9 && i < 14) {
                markers.push(
                  `markers=icon:${this.getMapMarkerImg(i)}%7C${
                    item.item.address.lat || item.item.lat
                  },${item.item.address.lng || item.item.lng}`
                );
              }
              // icon image doesn't support from 15, use letters
              if (i >= 14) {
                markers.push(
                  `markers=color:0xEBCF72%7Clabel:${letters[i - 14]}%7C${
                    item.item.address.lat || item.item.lat
                  },${item.item.address.lng || item.item.lng}`
                );
              }
            });

            mapImageUrl = ConfigService.config.google.multiRealPhoto.replace(
              '${path}',
              res.overview_polyline
            );
            mapImageUrl = mapImageUrl.replace('${appKey}', appKey);
            mapImageUrl = mapImageUrl + '&' + markers.join('&');

            resolve({ mapImageUrl, legs: res.legs });
          },
          (status) => {
            console.log(`Get direction error, status: `, status);
            resolve(null);
          }
        );
      }
    });
  }

  private getMapMarkerImg(index: number) {
    return `https://iti-images.s3.us-east-1.amazonaws.com/imgs/mapmarkers-pdf/yellow-${
      index + 1
    }.png`;
  }
}

export function hexToRgb(
  hex: string
): { r: number; g: number; b: number } | null {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  if (hex.length === 4) {
    hex = hex
      .slice(1)
      .split('')
      .map((c) => c + c)
      .join('');
  }

  // Remove the '#' symbol if present
  hex = hex.startsWith('#') ? hex.slice(1) : hex;

  // Check if the hex code is valid (6 characters long)
  if (hex.length !== 6) {
    return null; // Or throw an error: throw new Error("Invalid hex code");
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return { r, g, b };
}

export class PDFImgDataObject {
  constructor(
    public imageType: PDFImageFor,
    public base64: string,
    public width: number,
    public height: number,
    public attraction?: ItineraryAttraction | TripAttraction // public item?: VisitorEvent | VisitorPlace, // public preDescription?: string, // public postDescription?: string,
  ) {}
}

export enum PDFImageFor {
  top = 'top',
  item = 'item',
  logo = 'logo',
}

export enum PDFType {
  event = 'event',
  place = 'place',
  tour = 'tour',
  trip = 'trip',
}

export class ExportPDFCommandBase {
  hubName: string;
  type: PDFType;
  isPrintVersion: boolean;
  pdfIntro?: string;
  topImage?: string;
  logo?: string;
  mainColor?: string;
  appKey?: string;
  event?: ButtonTriggerEvent;
  progressCallback?: (progress: number) => void;
  completeCallback?: () => void;
}

export class ExportEventPlacePDFCommand extends ExportPDFCommandBase {
  items: VisitorEvent[] | VisitorPlace[];
  eventCalendarURL?: string;
  placeCalendarURL?: string;
  from?: Date;
  to?: Date;
}

export class ExportItineraryPDFCommand extends ExportPDFCommandBase {
  // trips: HubTour[];
  trips: Itinerary[];
  moreToursURL?: string;
}

export class ExportTripPDFCommand extends ExportPDFCommandBase {
  trip: UserTrip;
}

export class DrawTextCommand {
  text: string;
  x: number;
  y: number;
  width: number;
  maxLines: number;
  lineSpacing: number;
  color: string;
  fontStyle: string;
  fontSize: number;
}

export type DrawIconAndTextCommand = DrawTextCommand & {
  iconBase64: string;
  iconX: number;
  iconY: number;
  iconWidth: number;
  iconHeight: number;
};

export class DrawFlatLinkButtonCommand {
  btnText: string;
  x: number;
  y: number;
  width: number;
  height: number;
  textColor: string;
  btnColor: string;
  fontStyle: string;
  fontSize: number;
  link: string;
  isTextAtPageCenter: boolean;
}

export class DirectionData {
  mapImageUrl: string;
  legs: DirectionLeg[];
}

export class DirectionLeg {
  distance: { text: string; value: number };
  duration: { text: string; value: number };
}
